import {Injectable} from '@angular/core';
import {Shipment} from '../../domain/shipment';
import {RequestExecutor} from '../../util/request-executor';
import {ShipmentListResponse} from './shipment-list-response';

import {ShipmentQueryParameters} from './shipment-query-parameters';
import {Observable} from 'rxjs/internal/Observable';

import {map} from 'rxjs/operators';
import {Parcel} from '../../domain/parcel';
import {Deviation} from '../../domain/deviation';
import {HttpErrorResponse} from '@angular/common/http';
import {Event} from '../../domain/event';

@Injectable()
export class ShipmentListService {
  constructor(private request: RequestExecutor) {
  }

  async getShipment(query: string) {
    let response = await this.request.get('/shipments', {number: query, offset: 0, count: 1});

    if (response.TotalCount === 0) {
      throw new HttpErrorResponse({
        status: 404,
        error: {
          errorMessage: 'Sendingen finnes ikke',
          icon: 'error_outline',
          css_class: 'warn'
        }
      });
    }

    return this.extractShipments(response)[0]
  }

  async getShipments(parameters: ShipmentQueryParameters): Promise<ShipmentListResponse> {
    let response = await this.request.get('/shipments', parameters);

    return {
      totalShipmentCount: response.TotalCount,
      shipments: this.extractShipments(response)
    } as ShipmentListResponse;
  }

  async getDeviatedShipments(parameters: ShipmentQueryParameters): Promise<ShipmentListResponse> {
    let response = await this.request.get('/shipments/deviated', parameters);

    return {
      totalShipmentCount: response.TotalCount,
      shipments: this.extractShipments(response)
    } as ShipmentListResponse;
  }

  private extractShipments(response: any) {
    response.Shipments.forEach((shipment: Shipment) => {
      shipment.HasDeviations = shipment.DeviationCount != null ? shipment.DeviationCount > 0 : this.shipmentHasDeviations(shipment);
      shipment.Deviations.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());

      this.resolveStatus(shipment);

      shipment.Parcels.forEach((parcel: Parcel) => {
        parcel.Events.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());
        parcel.Deviations.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());
      });

      if (shipment.LastUserDisplayName == null) {
        this.resolveLastUser(shipment);
      }
    });

    return response.Shipments;
  }

  resolveStatus(shipment: Shipment) {
    if (shipment.OrderStatus == 'Expired') {
      return 'Expired';
    }

    let pickedUp = 0;
    let delivered = 0;

    shipment.Parcels.forEach((parcel: Parcel) => {
      if (parcel.Events.length !== 0) {
        parcel.Events.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());

        switch (parcel.Events[0].Type) {
          case 'PickUp':
            pickedUp++;
            break;
          case 'Deliver':
            delivered++;
            break;
        }
      }
    });

    if (shipment.Parcels.length > 0 && shipment.Parcels.length === delivered) {
      shipment.OrderStatus = 'Finished';
    } else if (pickedUp > 0 || delivered > 0) {
      shipment.OrderStatus = 'Started';
    }
  }

  private shipmentHasDeviations(shipment: Shipment) {
    if (shipment.Deviations.length > 0) {
      return true;
    }

    return shipment.Parcels.filter(p => p.Deviations.length > 0).length > 0;
  }

  private resolveLastUser(shipment: Shipment) {
    let lastDeviations: Deviation[] = [];
    let lastEvents: Event[] = [];

    if (shipment.Deviations.length > 0) {
      lastDeviations.push(shipment.Deviations[0]);
    }

    shipment.Parcels.forEach(p => {
      if (p.Deviations.length > 0) {
        lastDeviations.push(p.Deviations[0]);
      }

      if (p.Events.length > 0) {
        lastEvents.push(p.Events[0]);
      }
    });

    lastDeviations.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());
    lastEvents.sort((a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime());

    if (lastDeviations.length > 0 && lastEvents.length > 0) {
      if (new Date(lastDeviations[0].CreatedAt).getTime() > new Date(lastEvents[0].CreatedAt).getTime()) {
        shipment.LastUserDisplayName = lastDeviations[0].UserDisplayName;
      } else {
        shipment.LastUserDisplayName = lastEvents[0].UserDisplayName;
      }
    } else if (lastDeviations.length > 0) {
      shipment.LastUserDisplayName = lastDeviations[0].UserDisplayName;
    } else if (lastEvents.length > 0) {
      shipment.LastUserDisplayName = lastEvents[0].UserDisplayName;
    }
  }
}
